import { GFAFullDetails } from "@dune/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import { nullableNumberSchema } from "../../utils/zodUtils";

export const createGfaFinancingSchema = () =>
	z
		.object({
			// Prix de vente et coûts
			estimatedSalePriceHT: nullableNumberSchema,
			estimatedSalePriceTTC: nullableNumberSchema,
			costPriceHT: nullableNumberSchema,
			costPriceTTC: nullableNumberSchema,

			// Détail des coûts
			landCostHT: nullableNumberSchema,
			landCostTTC: nullableNumberSchema,
			studiesAndTechnicalFeesHT: nullableNumberSchema,
			studiesAndTechnicalFeesTTC: nullableNumberSchema,
			constructionCostsHT: nullableNumberSchema,
			constructionCostsTTC: nullableNumberSchema,
			contingenciesHT: nullableNumberSchema,
			contingenciesTTC: nullableNumberSchema,
			developerManagementFeesHT: nullableNumberSchema,
			developerManagementFeesTTC: nullableNumberSchema,
			marketingCostsHT: nullableNumberSchema,
			marketingCostsTTC: nullableNumberSchema,
			insuranceAndMiscellaneousHT: nullableNumberSchema,
			insuranceAndMiscellaneousTTC: nullableNumberSchema,

			contributionAmount: nullableNumberSchema,
			bankFinancings: z.array(
				z.object({
					amount: nullableNumberSchema,
					rate: nullableNumberSchema,
					durationInMonths: nullableNumberSchema,
				}),
			),
			otherFinancings: z.array(
				z.object({
					financingSource: z.enum(["CROWDFUNDING", "DATION", "OTHER"]),
					amount: nullableNumberSchema,
				}),
			),
			fundRaisings: z.array(
				z.object({
					name: z.string().min(1).optional(),
					percentage: nullableNumberSchema,
				}),
			),
			numberOfLots: nullableNumberSchema,
			preCommercializationAmount: nullableNumberSchema,
			preCommercializationPercentage: nullableNumberSchema,
			clearedPreCommercializationAmount: nullableNumberSchema,
			clearedPreCommercializationPercentage: nullableNumberSchema,
		})
		.refine(
			(data) =>
				(data.estimatedSalePriceHT !== undefined &&
					data.estimatedSalePriceHT !== null) ||
				(data.estimatedSalePriceTTC !== undefined &&
					data.estimatedSalePriceTTC !== null),
			{
				message: "Au moins un montant (HT ou TTC) doit être renseigné",
				path: ["estimatedSalePriceTTC"],
			},
		)
		.refine(
			(data) =>
				(data.costPriceHT !== undefined && data.costPriceHT !== null) ||
				(data.costPriceTTC !== undefined && data.costPriceTTC !== null),
			{
				message: "Au moins un montant (HT ou TTC) doit être renseigné",
				path: ["costPriceTTC"],
			},
		)
		.refine(
			(data) => {
				if (
					data.estimatedSalePriceHT !== undefined &&
					data.estimatedSalePriceHT !== null &&
					data.estimatedSalePriceTTC !== undefined &&
					data.estimatedSalePriceTTC !== null
				) {
					return data.estimatedSalePriceHT < data.estimatedSalePriceTTC;
				}
				return true;
			},
			{
				message: "Le montant HT doit être inférieur au montant TTC",
				path: ["estimatedSalePriceTTC"],
			},
		)
		.refine(
			(data) => {
				if (
					data.estimatedSalePriceHT !== undefined &&
					data.estimatedSalePriceHT !== null
				) {
					return data.estimatedSalePriceHT > 0;
				}
				return true;
			},
			{
				message: "Le montant HT doit être positif",
				path: ["estimatedSalePriceHT"],
			},
		)
		.refine(
			(data) => {
				if (
					data.estimatedSalePriceTTC !== undefined &&
					data.estimatedSalePriceTTC !== null
				) {
					return data.estimatedSalePriceTTC > 0;
				}
				return true;
			},
			{
				message: "Le montant TTC doit être positif",
				path: ["estimatedSalePriceTTC"],
			},
		)
		.refine(
			(data) => {
				if (
					data.costPriceHT !== undefined &&
					data.costPriceHT !== null &&
					data.costPriceTTC !== undefined &&
					data.costPriceTTC !== null
				) {
					return data.costPriceHT < data.costPriceTTC;
				}
				return true;
			},
			{
				message: "Le montant HT doit être inférieur au montant TTC",
				path: ["costPriceTTC"],
			},
		)
		.refine(
			(data) => {
				if (data.costPriceHT !== undefined && data.costPriceHT !== null) {
					return data.costPriceHT > 0;
				}
				return true;
			},
			{
				message: "Le montant HT doit être positif",
				path: ["costPriceHT"],
			},
		)
		.refine(
			(data) => {
				if (data.costPriceTTC !== undefined && data.costPriceTTC !== null) {
					return data.costPriceTTC > 0;
				}
				return true;
			},
			{
				message: "Le montant TTC doit être positif",
				path: ["costPriceTTC"],
			},
		)
		.refine(
			(data) => {
				if (
					data.landCostHT !== undefined &&
					data.landCostHT !== null &&
					data.landCostTTC !== undefined &&
					data.landCostTTC !== null
				) {
					return data.landCostHT < data.landCostTTC;
				}
				return true;
			},
			{
				message: "Le montant HT doit être inférieur au montant TTC",
				path: ["landCostTTC"],
			},
		)
		.refine(
			(data) => {
				if (
					data.studiesAndTechnicalFeesHT !== undefined &&
					data.studiesAndTechnicalFeesHT !== null &&
					data.studiesAndTechnicalFeesTTC !== undefined &&
					data.studiesAndTechnicalFeesTTC !== null
				) {
					return (
						data.studiesAndTechnicalFeesHT < data.studiesAndTechnicalFeesTTC
					);
				}
				return true;
			},
			{
				message: "Le montant HT doit être inférieur au montant TTC",
				path: ["studiesAndTechnicalFeesTTC"],
			},
		)
		.refine(
			(data) => {
				if (
					data.constructionCostsHT !== undefined &&
					data.constructionCostsHT !== null &&
					data.constructionCostsTTC !== undefined &&
					data.constructionCostsTTC !== null
				) {
					return data.constructionCostsHT < data.constructionCostsTTC;
				}
				return true;
			},
			{
				message: "Le montant HT doit être inférieur au montant TTC",
				path: ["constructionCostsTTC"],
			},
		)
		.refine(
			(data) => {
				if (
					data.contingenciesHT !== undefined &&
					data.contingenciesHT !== null &&
					data.contingenciesTTC !== undefined &&
					data.contingenciesTTC !== null
				) {
					return data.contingenciesHT < data.contingenciesTTC;
				}
				return true;
			},
			{
				message: "Le montant HT doit être inférieur au montant TTC",
				path: ["contingenciesTTC"],
			},
		)
		.refine(
			(data) => {
				if (
					data.developerManagementFeesHT !== undefined &&
					data.developerManagementFeesHT !== null &&
					data.developerManagementFeesTTC !== undefined &&
					data.developerManagementFeesTTC !== null
				) {
					return (
						data.developerManagementFeesHT < data.developerManagementFeesTTC
					);
				}
				return true;
			},
			{
				message: "Le montant HT doit être inférieur au montant TTC",
				path: ["developerManagementFeesTTC"],
			},
		)
		.refine(
			(data) => {
				if (
					data.marketingCostsHT !== undefined &&
					data.marketingCostsHT !== null &&
					data.marketingCostsTTC !== undefined &&
					data.marketingCostsTTC !== null
				) {
					return data.marketingCostsHT < data.marketingCostsTTC;
				}
				return true;
			},
			{
				message: "Le montant HT doit être inférieur au montant TTC",
				path: ["marketingCostsTTC"],
			},
		)
		.refine(
			(data) => {
				if (
					data.insuranceAndMiscellaneousHT !== undefined &&
					data.insuranceAndMiscellaneousHT !== null &&
					data.insuranceAndMiscellaneousTTC !== undefined &&
					data.insuranceAndMiscellaneousTTC !== null
				) {
					return (
						data.insuranceAndMiscellaneousHT < data.insuranceAndMiscellaneousTTC
					);
				}
				return true;
			},
			{
				message: "Le montant HT doit être inférieur au montant TTC",
				path: ["insuranceAndMiscellaneousTTC"],
			},
		);

export const gfaFinancingSchema = createGfaFinancingSchema();
export type GFAFinancingsFormData = z.infer<
	ReturnType<typeof createGfaFinancingSchema>
>;

export const gfaFinancingsDefaultValues: GFAFinancingsFormData = {
	estimatedSalePriceHT: null,
	estimatedSalePriceTTC: null,
	costPriceHT: null,
	costPriceTTC: null,
	landCostHT: null,
	landCostTTC: null,
	studiesAndTechnicalFeesHT: null,
	studiesAndTechnicalFeesTTC: null,
	constructionCostsHT: null,
	constructionCostsTTC: null,
	contingenciesHT: null,
	contingenciesTTC: null,
	developerManagementFeesHT: null,
	developerManagementFeesTTC: null,
	marketingCostsHT: null,
	marketingCostsTTC: null,
	insuranceAndMiscellaneousHT: null,
	insuranceAndMiscellaneousTTC: null,
	contributionAmount: null,
	bankFinancings: [],
	otherFinancings: [],
	fundRaisings: [],
	numberOfLots: null,
	preCommercializationAmount: null,
	preCommercializationPercentage: null,
	clearedPreCommercializationAmount: null,
	clearedPreCommercializationPercentage: null,
};

export function useGFAFinancingsForm(gfa: GFAFullDetails | null) {
	const defaultValues = gfa
		? mapGFAFinancingToFormData(gfa)
		: gfaFinancingsDefaultValues;

	const schema = createGfaFinancingSchema();
	const form = useForm<GFAFinancingsFormData>({
		mode: "all",
		defaultValues,
		resolver: zodResolver(schema),
	});
	const { reset } = form;
	useEffect(() => {
		gfa && reset(mapGFAFinancingToFormData(gfa));
	}, [reset, gfa]);
	return { form, defaultValues };
}

const mapGFAFinancingToFormData = (
	gfa: GFAFullDetails,
): Partial<GFAFinancingsFormData> => {
	const financings = gfa.gfaFinancings;
	if (!financings) return gfaFinancingsDefaultValues;
	return {
		estimatedSalePriceHT: financings.estimatedSalePriceHT ?? null,
		estimatedSalePriceTTC: financings.estimatedSalePriceTTC ?? null,
		costPriceHT: financings.costPriceHT ?? null,
		costPriceTTC: financings.costPriceTTC ?? null,
		landCostHT: financings.landCostHT ?? null,
		landCostTTC: financings.landCostTTC ?? null,
		studiesAndTechnicalFeesHT: financings.studiesAndTechnicalFeesHT ?? null,
		studiesAndTechnicalFeesTTC: financings.studiesAndTechnicalFeesTTC ?? null,
		constructionCostsHT: financings.constructionCostsHT ?? null,
		constructionCostsTTC: financings.constructionCostsTTC ?? null,
		contingenciesHT: financings.contingenciesHT ?? null,
		contingenciesTTC: financings.contingenciesTTC ?? null,
		developerManagementFeesHT: financings.developerManagementFeesHT ?? null,
		developerManagementFeesTTC: financings.developerManagementFeesTTC ?? null,
		marketingCostsHT: financings.marketingCostsHT ?? null,
		marketingCostsTTC: financings.marketingCostsTTC ?? null,
		insuranceAndMiscellaneousHT: financings.insuranceAndMiscellaneousHT ?? null,
		insuranceAndMiscellaneousTTC:
			financings.insuranceAndMiscellaneousTTC ?? null,
		contributionAmount: financings.contributionAmount ?? null,
		bankFinancings: financings.bankFinancings.map((bankFinancing) => ({
			amount: bankFinancing.amount ?? null,
			rate: bankFinancing.rate ?? null,
			durationInMonths: bankFinancing.durationInMonths ?? null,
		})),
		otherFinancings: financings.otherFinancings.map((otherFinancing) => ({
			financingSource: otherFinancing.financingSource,
			amount: otherFinancing.amount ?? null,
		})),
		fundRaisings: financings.fundRaisings.map((fundRaising) => ({
			name: fundRaising.name ?? undefined,
			percentage: fundRaising.percentage ?? null,
		})),
		numberOfLots: financings.numberOfLots ?? null,
		preCommercializationAmount: financings.preCommercializationAmount ?? null,
		preCommercializationPercentage:
			financings.preCommercializationPercentage ?? null,
		clearedPreCommercializationAmount:
			financings.clearedPreCommercializationAmount ?? null,
		clearedPreCommercializationPercentage:
			financings.clearedPreCommercializationPercentage ?? null,
	};
};
